/**
 * Custom React Hooks for Mixpanel Analytics
 */

import { useCallback, useEffect } from 'react';
import mixpanelService from '../mixpanel.service';
import type {
  MixpanelEventName,
  MixpanelEventMap,
  MixpanelUserProperties,
} from '../types';

/**
 * Main hook for tracking events
 */
export const useMixpanel = () => {
  const track = useCallback(
    <T extends MixpanelEventName>(
      eventName: T,
      properties?: MixpanelEventMap[T]
    ) => {
      mixpanelService.track(eventName, properties);
    },
    []
  );

  const identify = useCallback((userId: string | number) => {
    mixpanelService.identify(userId);
  }, []);

  const setUserProperties = useCallback((properties: MixpanelUserProperties) => {
    mixpanelService.setUserProperties(properties);
  }, []);

  const reset = useCallback(() => {
    mixpanelService.reset();
  }, []);

  const trackPageView = useCallback((pageName?: string) => {
    mixpanelService.trackPageView(pageName);
  }, []);

  return {
    track,
    identify,
    setUserProperties,
    reset,
    trackPageView,
    incrementUserProperty: mixpanelService.incrementUserProperty.bind(mixpanelService),
    setUserPropertyOnce: mixpanelService.setUserPropertyOnce.bind(mixpanelService),
    registerSuperProperties: mixpanelService.registerSuperProperties.bind(mixpanelService),
    timeEvent: mixpanelService.timeEvent.bind(mixpanelService),
    getDistinctId: mixpanelService.getDistinctId.bind(mixpanelService),
  };
};

/**
 * Hook to track page views automatically
 */
export const usePageTracking = (pageName?: string) => {
  const { trackPageView } = useMixpanel();

  useEffect(() => {
    trackPageView(pageName);
  }, [pageName, trackPageView]);
};

/**
 * Hook to track authentication events
 */
export const useAuthTracking = () => {
  const { track, identify, setUserProperties, reset } = useMixpanel();

  const trackLogin = useCallback(
    (properties: MixpanelEventMap['Portal user_logged_in']) => {
      console.log('[useAuthTracking] trackLogin called with properties:', properties);

      // Identify user first
      identify(properties.user_id);
      console.log('[useAuthTracking] User identified:', properties.user_id);

      // Set user properties
      setUserProperties({
        $name: properties.full_name,
        tenant_id: properties.tenant_id,
        user_id: properties.user_id,
        full_name: properties.full_name,
        roles: properties.roles,
        last_login: new Date().toISOString(),
      });
      console.log('[useAuthTracking] User properties set');

      // Track login event
      track('Portal user_logged_in', properties);
      console.log('[useAuthTracking] Login event tracked');
    },
    [track, identify, setUserProperties]
  );

  const trackLoginFailed = useCallback(
    (properties: MixpanelEventMap['Portal user_login_failed']) => {
      track('Portal user_login_failed', properties);
    },
    [track]
  );

  const trackLogout = useCallback(
    (properties: MixpanelEventMap['Portal user_logged_out']) => {
      track('Portal user_logged_out', properties);
      // Reset user data after tracking logout
      reset();
    },
    [track, reset]
  );

  return {
    trackLogin,
    trackLoginFailed,
    trackLogout,
  };
};

/**
 * Hook to track invoice events
 */
export const useInvoiceTracking = () => {
  const { track } = useMixpanel();

  const trackInvoiceViewed = useCallback(
    (properties: MixpanelEventMap['Portal invoice_viewed']) => {
      track('Portal invoice_viewed', properties);
    },
    [track]
  );

  const trackCreditNoteViewed = useCallback(
    (properties: MixpanelEventMap['Portal credit_note_viewed']) => {
      track('Portal credit_note_viewed', properties);
    },
    [track]
  );

  const trackInvoiceSubmitted = useCallback(
    (properties: MixpanelEventMap['Portal invoice_submitted']) => {
      // Increment user's invoice counter
      mixpanelService.incrementUserProperty('invoices_created', 1);

      // Track the event
      track('Portal invoice_submitted', properties);
    },
    [track]
  );

  const trackInvoiceSubmissionFailed = useCallback(
    (properties: MixpanelEventMap['Portal invoice_submission_failed']) => {
      track('Portal invoice_submission_failed', properties);
    },
    [track]
  );

  return {
    trackInvoiceViewed,
    trackCreditNoteViewed,
    trackInvoiceSubmitted,
    trackInvoiceSubmissionFailed,
  };
};

/**
 * Hook to track certificate events
 */
export const useCertificateTracking = () => {
  const { track } = useMixpanel();

  const trackCertificateModalOpened = useCallback(
    (properties: MixpanelEventMap['Portal certificate_modal_opened']) => {
      track('Portal certificate_modal_opened', properties);
    },
    [track]
  );

  const trackCertificateHasSignature = useCallback(
    (properties: MixpanelEventMap['Portal certificate_has_signature']) => {
      track('Portal certificate_has_signature', properties);
    },
    [track]
  );

  const trackCertificateNeedsSignature = useCallback(
    (properties: MixpanelEventMap['Portal certificate_needs_signature']) => {
      track('Portal certificate_needs_signature', properties);
    },
    [track]
  );

  const trackCertificateGotoConfiguration = useCallback(
    (properties: MixpanelEventMap['Portal certificate_goto_configuration']) => {
      track('Portal certificate_goto_configuration', properties);
    },
    [track]
  );

  const trackCertificateGuideStepViewed = useCallback(
    (properties: MixpanelEventMap['Portal certificate_guide_step_viewed']) => {
      track('Portal certificate_guide_step_viewed', properties);
    },
    [track]
  );

  const trackCertificateGuideCompleted = useCallback(
    (properties: MixpanelEventMap['Portal certificate_guide_completed']) => {
      track('Portal certificate_guide_completed', properties);
    },
    [track]
  );

  const trackCertificateLinkClicked = useCallback(
    (properties: MixpanelEventMap['Portal certificate_link_clicked']) => {
      track('Portal certificate_link_clicked', properties);
    },
    [track]
  );

  return {
    trackCertificateModalOpened,
    trackCertificateHasSignature,
    trackCertificateNeedsSignature,
    trackCertificateGotoConfiguration,
    trackCertificateGuideStepViewed,
    trackCertificateGuideCompleted,
    trackCertificateLinkClicked,
  };
};

/**
 * Hook to track UI interaction events
 */
export const useUITracking = () => {
  const { track } = useMixpanel();

  const trackNavigationClick = useCallback(
    (properties: MixpanelEventMap['Portal navigation_click']) => {
      track('Portal navigation_click', properties);
    },
    [track]
  );

  const trackTopbarClick = useCallback(
    (properties: MixpanelEventMap['Portal topbar_click']) => {
      track('Portal topbar_click', properties);
    },
    [track]
  );

  const trackCompanySwitched = useCallback(
    (properties: MixpanelEventMap['Portal company_switched']) => {
      // Update super properties with new company
      mixpanelService.registerSuperProperties({
        company_id: properties.new_company_id,
      });

      // Update user properties
      mixpanelService.setUserProperties({
        company_id: properties.new_company_id,
        company_name: properties.new_company_name,
      });

      // Track the event
      track('Portal company_switched', properties);
    },
    [track]
  );

  const trackModalOpened = useCallback(
    (properties: MixpanelEventMap['Portal modal_opened']) => {
      track('Portal modal_opened', properties);
    },
    [track]
  );

  const trackFormSubmitted = useCallback(
    (properties: MixpanelEventMap['Portal form_submitted']) => {
      track('Portal form_submitted', properties);
    },
    [track]
  );

  return {
    trackNavigationClick,
    trackTopbarClick,
    trackCompanySwitched,
    trackModalOpened,
    trackFormSubmitted,
  };
};

/**
 * Hook to track timed events
 */
export const useTimedEvent = (eventName: string) => {
  const { track, timeEvent } = useMixpanel();

  useEffect(() => {
    // Start timing when component mounts
    timeEvent(eventName);
  }, [eventName, timeEvent]);

  const complete = useCallback(
    (properties?: any) => {
      track(eventName as MixpanelEventName, properties);
    },
    [eventName, track]
  );

  return { complete };
};

export default useMixpanel;
